import type { ReactNode } from 'react'
import { PixelIcon } from '@/components/ui/PixelIcon'

interface HUDProps {
  levelName: string
  slides: number
  stepTarget: number
  coins: number
  totalCoins: number
  timeMs: number
  onPause: () => void
}

function fmt(ms: number) {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  return `${m}:${String(s % 60).padStart(2, '0')}`
}

function Chip({ icon, children }: { icon: Parameters<typeof PixelIcon>[0]['name']; children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-[var(--surface-sunken)] px-2 py-1 sm:px-3 sm:py-1.5 shadow-sunken">
      <PixelIcon name={icon} size={14} className="shrink-0" />
      <span className="font-display text-xs sm:text-sm font-bold tabular-nums">{children}</span>
    </div>
  )
}

export function HUD({ levelName, slides, stepTarget, coins, totalCoins, timeMs, onPause }: HUDProps) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 pt-[calc(0.75rem+var(--safe-top))] sm:pt-4">
      <div className="min-w-0">
        <div className="font-display font-bold text-sm sm:text-base truncate">{levelName}</div>
      </div>
      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        <Chip icon="steps">
          {slides}
          <span className="text-[var(--text-faint)]">/{stepTarget}</span>
        </Chip>
        <Chip icon="coin">
          <span style={{ color: 'var(--coin)' }}>
            {coins}/{totalCoins}
          </span>
        </Chip>
        <Chip icon="clock">{fmt(timeMs)}</Chip>
        <button
          aria-label="Pause"
          onClick={onPause}
          className="grid place-items-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[var(--surface-raised)] shadow-emboss text-[var(--text-dim)] hover:text-[var(--text)] active:scale-95"
        >
          <PixelIcon name="pause" size={17} />
        </button>
      </div>
    </div>
  )
}
